/**
 * Hook to get preview content from a chip's Evidence chat.
 *
 * Extracts the latest assistant response from a chip's linked chat,
 * truncates it for preview, and extracts citation references.
 */

import { chatStateAtom } from '@/atoms/chatAtoms';
import { sessionChipsStateAtom } from '@/atoms/evidenceChipsAtoms';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

// Max characters for preview content
const PREVIEW_MAX_CHARS = 300;

// Regex to find citation markers like [1], [2], etc.
const CITATION_REGEX = /\[(\d+)\]/g;

interface Citation {
  number: number;
  text: string;
}

interface ChipAnswerPreview {
  /** Whether the chip has an answer */
  hasAnswer: boolean;
  /** Truncated preview text */
  previewText: string | null;
  /** Full answer text */
  fullText: string | null;
  /** Citations found in the preview portion */
  citations: Citation[];
  /** Whether the preview was truncated */
  isTruncated: boolean;
  /** Loading state */
  isLoading: boolean;
}

interface UseChipAnswerPreviewOptions {
  sessionId: string | null;
  chipId: string;
}

export function useChipAnswerPreview({
  sessionId,
  chipId,
}: UseChipAnswerPreviewOptions): ChipAnswerPreview {
  // Get chip-to-chat mapping
  const sessionState = useAtomValue(sessionChipsStateAtom(sessionId ?? 'no-session'));
  const chatId = sessionState.chipToChatMap[chipId];

  // Get chat state to find messages
  const chatState = useAtomValue(chatStateAtom);

  return useMemo(() => {
    // No chat linked to this chip
    if (!chatId) {
      return {
        hasAnswer: false,
        previewText: null,
        fullText: null,
        citations: [],
        isTruncated: false,
        isLoading: false,
      };
    }

    // Find the conversation for this chat
    // Chat state keys are typically `${sessionId}-${chatId}` or just chatId for sessionless
    const stateKey = sessionId ? `${sessionId}-${chatId}` : chatId;
    const conversation = chatState[stateKey];

    if (!conversation) {
      return {
        hasAnswer: false,
        previewText: null,
        fullText: null,
        citations: [],
        isTruncated: false,
        isLoading: false,
      };
    }

    // Check if still streaming
    const isLoading = !!conversation.streamingState;

    // Find text items (assistant responses)
    const textItems = conversation.timeline.filter((entry) => entry.type === 'text_item');

    if (textItems.length === 0) {
      return {
        hasAnswer: false,
        previewText: null,
        fullText: null,
        citations: [],
        isTruncated: false,
        isLoading,
      };
    }

    // Get the latest text item (assistant message)
    const latestTextItem = textItems[textItems.length - 1];
    const fullText = latestTextItem?.type === 'text_item' ? latestTextItem.content : null;

    if (!fullText) {
      return {
        hasAnswer: false,
        previewText: null,
        fullText: null,
        citations: [],
        isTruncated: false,
        isLoading,
      };
    }

    // Truncate for preview
    const isTruncated = fullText.length > PREVIEW_MAX_CHARS;
    const previewText = isTruncated
      ? fullText.slice(0, PREVIEW_MAX_CHARS).trim() + '...'
      : fullText;

    // Extract citations from the preview portion
    const citationsInPreview: Citation[] = [];
    const matches = previewText.matchAll(CITATION_REGEX);

    for (const match of matches) {
      const matchGroup = match[1];
      if (!matchGroup) continue;
      const citationNumber = parseInt(matchGroup, 10);
      // For now, just use placeholder text - real implementation would look up source
      citationsInPreview.push({
        number: citationNumber,
        text: `Reference ${citationNumber}`,
      });
    }

    // Deduplicate citations by number
    const uniqueCitations = Array.from(
      new Map(citationsInPreview.map((c) => [c.number, c])).values()
    ).sort((a, b) => a.number - b.number);

    return {
      hasAnswer: true,
      previewText,
      fullText,
      citations: uniqueCitations,
      isTruncated,
      isLoading,
    };
  }, [chatId, sessionId, chatState]);
}
